import { CommonModule } from '@angular/common';
import { Component, ContentChild, EventEmitter, Input, OnChanges, OnDestroy, Output, TemplateRef, ViewChild } from '@angular/core';
import { NbButtonModule, NbIconModule, NbPopoverDirective, NbPopoverModule, NbTooltipModule } from '@nebular/theme';
import { ConversionShared } from 'src/app/models/conversion.model';
import { XeroSendControlComponent } from '../xero-send-control/xero-send-control.component';
import { buildConversionRowView, ConversionRowView } from './conversion-row-view';

export type ConversionRowLink = 'original' | 'csv' | 'xlsx';
/** Links fetched as a blob so the row can show a loading state until the download lands */
export type ConversionRowDownload = Exclude<ConversionRowLink, 'original'>;

/**
 * File Row v2 ("Quiet Row"). One row per conversion across History, Convert
 * step 3 and Admin Files: status disc + name/meta on the left, the concluding
 * actions (CSV/XLSX download, Send to Xero) as buttons on the right, and
 * everything secondary behind the ⋯ menu.
 *
 * Hosts customise via inputs/outputs plus two optional templates:
 *   <ng-template #menuExtra>  → extra items inside the ⋯ menu (e.g. AI correction)
 *   <ng-template #trailing>   → extra content before the ⋯ button (e.g. a loader)
 */
@Component({
  selector: 'app-conversion-row',
  standalone: true,
  imports: [CommonModule, NbButtonModule, NbIconModule, NbPopoverModule, NbTooltipModule, XeroSendControlComponent],
  templateUrl: './conversion-row.component.html',
  styleUrls: ['./conversion-row.component.scss'],
})
export class ConversionRowComponent implements OnChanges, OnDestroy {
  @Input() conversion: ConversionShared;
  /** Admin mode: shows user/country meta, sensitive marker; hides the Xero control */
  @Input() isAdmin = false;
  /** Show a Delete item in the ⋯ menu (emits `remove`) */
  @Input() canDelete = false;
  /** Render the original-file download as an always-visible icon button (admin correction flow) */
  @Input() showOriginalAction = false;
  /** Render view-details as an always-visible icon button */
  @Input() showDetailsAction = false;

  @Output() viewDetails = new EventEmitter<void>();
  @Output() remove = new EventEmitter<void>();
  /** A download link was opened (admin uses this for file-viewed/correction tracking) */
  @Output() linkOpened = new EventEmitter<ConversionRowLink>();

  @ContentChild('menuExtra') menuExtra?: TemplateRef<unknown>;
  @ContentChild('trailing') trailing?: TemplateRef<unknown>;
  @ViewChild(NbPopoverDirective) menuPopover?: NbPopoverDirective;

  view: ConversionRowView;

  private timeoutTimer?: ReturnType<typeof setTimeout>;

  // Keeps the relative upload date ("5m ago") from going stale. One ticker for
  // every mounted row, not one per row: a long list would otherwise schedule a
  // timer — and so a change-detection pass — per visible row every minute.
  private static readonly mounted = new Set<ConversionRowComponent>();
  private static ticker?: ReturnType<typeof setInterval>;

  constructor() {
    ConversionRowComponent.mounted.add(this);
    if (!ConversionRowComponent.ticker) {
      ConversionRowComponent.ticker = setInterval(() => ConversionRowComponent.mounted.forEach((row) => row.rebuildView()), 60_000);
    }
  }

  ngOnChanges() {
    this.rebuildView();
  }

  ngOnDestroy() {
    clearTimeout(this.timeoutTimer);
    ConversionRowComponent.mounted.delete(this);
    if (!ConversionRowComponent.mounted.size) {
      clearInterval(ConversionRowComponent.ticker);
      ConversionRowComponent.ticker = undefined;
    }
  }

  onLink(kind: ConversionRowLink) {
    this.linkOpened.emit(kind);
  }

  /** Downloads in flight — drives the CSV/XLSX button spinners */
  busy = new Set<ConversionRowDownload>();

  /**
   * CSV/XLSX are attachment downloads, which give no feedback until the
   * browser's download bar appears — so fetch the file ourselves, show a
   * spinner until it lands, then hand the blob to the browser. Falls back to
   * plain link navigation if the fetch is blocked (e.g. CORS).
   *
   * The original PDF is a plain new-tab anchor and never comes through here:
   * a synchronous anchor is the only new-tab open that popup blockers can't
   * stop, and the tab itself is the loading feedback.
   */
  async onDownload(event: MouseEvent, kind: ConversionRowDownload, url: string) {
    // Modified clicks (new tab / copy link) keep native anchor behaviour.
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      this.linkOpened.emit(kind);
      return;
    }
    event.preventDefault();
    if (this.busy.has(kind)) return;
    this.linkOpened.emit(kind);
    this.busy.add(kind);
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = this.downloadName(kind);
      a.click();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch {
      window.location.assign(url);
    } finally {
      this.busy.delete(kind);
    }
  }

  private downloadName(kind: ConversionRowDownload): string {
    const base = (this.view?.fileName || 'download').replace(/\.[^.]+$/, '');
    return kind === 'xlsx' ? `${base}.xlsx` : `${base}.csv`;
  }

  /** Close the ⋯ popover when any (enabled) menu item — including projected ones — is clicked */
  onMenuClick(event: Event) {
    if ((event.target as HTMLElement).closest('button, a')) {
      this.menuPopover?.hide();
    }
  }

  private rebuildView() {
    clearTimeout(this.timeoutTimer);
    if (!this.conversion) return;
    this.view = buildConversionRowView(this.conversion, { isAdmin: this.isAdmin });

    // A processing row must flip to Timeout on its own once the window passes.
    if (this.view.msUntilTimeout !== null) {
      this.timeoutTimer = setTimeout(() => this.rebuildView(), this.view.msUntilTimeout + 250);
    }
  }
}
